import { SESSION_COMPANY } from '@mm/api-routes';
import { UTMTags } from '@mm/hooks';
import { ObjectUtils, extractAffiliateData } from '@mm/utils';
import { gtag } from '@mm/plugins/GoogleAnalytics';
import ReactGA from 'react-ga4';
import { Dispatch } from 'redux';
import AuthenticatedController from '../controllers/AuthenticatedController';
import SessionController from '../controllers/SessionController';
import commonHandler from '../errorHandlers/common-handler';
import { create as createSessionHandler } from '../errorHandlers/session-handler';
import { SessionObject } from '../interfaces/session';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';
import UsersController from '../controllers/UsersController';
import { omit } from 'lodash';

const ErrorMessages = {
  403: 'Invalid Email/Password pair',
};

class SessionCRUDActions extends CrudActionContainer {
  static SIGN_UP = 'SIGN_UP';
  static LOGIN = 'LOGIN';

  signupAction: string;
  loginAction: string;
  clearSessionAction: string;
  setInviteTokenAction: string;
  resetInviteTokenAction: string;
  getMyUserAction: string;

  constructor() {
    super('SESSION');

    this.clearSessionAction = this.makeActionKey('CLEAR_SESSION_ACTION');
    this.setInviteTokenAction = this.makeActionKey('SET_INVITE_TOKEN');
    this.resetInviteTokenAction = this.makeActionKey('RESET_INVITE_TOKEN');
    this.getMyUserAction = this.makeActionKey('GET_MY_USER');

    this.signupAction = this.makeActionKey(SessionCRUDActions.SIGN_UP);
    this.loginAction = this.makeActionKey(SessionCRUDActions.LOGIN);
  }

  signUp(...args: Parameters<typeof SessionController.signup>) {
    return async dispatch => {
      dispatch(this.busy(this.signupAction));
      try {
        const affiliateData = extractAffiliateData();
        const affiliatePayload = {
          affiliateCode: affiliateData?.code,
          affiliateVisitorId: affiliateData?.visitorId,
        };
        const [payload, ...rest] = args;
        // allow outer scope override inferred affiliate payload
        const session = await SessionController.signup(
          { ...affiliatePayload, ...payload },
          ...rest,
        );
        UTMTags.clear();

        if (!args[0].inviteToken) {
          ReactGA.set({ page: '/new-registrant' });
          gtag('event', 'mobilemonkey-signup');
        }

        AuthenticatedController.storeSession(session);
        dispatch(this.retrieved(session));
        return session;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  login: (
    ...args: Parameters<typeof SessionController.login>
  ) => (dispatch: Dispatch<StoreState>) => Promise<SessionObject> =
    (...args) =>
    async dispatch => {
      dispatch(this.busy(this.loginAction));
      try {
        const session = await SessionController.login(...args);
        AuthenticatedController.storeSession(session);
        dispatch(this.retrieved(session));
        return session;
      } finally {
        dispatch(this.unbusy());
      }
    };

  requestResetPassword(
    ...args: Parameters<typeof SessionController.requestResetPassword>
  ) {
    return async dispatch => {
      dispatch(this.busy());

      try {
        return await SessionController.requestResetPassword(...args);
      } catch (err) {
        commonHandler(dispatch)(err);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  confirmResetPassword(
    ...args: Parameters<typeof SessionController.confirmResetPassword>
  ) {
    return async dispatch => {
      dispatch(this.busy());

      try {
        return await SessionController.confirmResetPassword(...args);
      } catch (err) {
        commonHandler(dispatch)(err);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  validateResetPasswordToken(
    ...args: Parameters<typeof SessionController.validateResetPasswordToken>
  ) {
    return async dispatch => {
      dispatch(this.busy());

      try {
        return await SessionController.validateResetPasswordToken(...args);
      } catch (err) {
        commonHandler(dispatch)(err);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  create(...args: Parameters<typeof SessionController.loginFacebook>) {
    return dispatch => {
      dispatch(this.busy(this.createAction));

      return SessionController.loginFacebook(...args)
        .then(response => {
          AuthenticatedController.storeSession(response);
          dispatch(this.retrieved(response));

          return response;
        })
        .catch(createSessionHandler(dispatch))
        .catch(commonHandler(dispatch));
    };
  }

  createSemrush(...args: Parameters<typeof SessionController.loginSemrush>) {
    return dispatch => {
      dispatch(this.busy(this.createAction));

      return SessionController.loginSemrush(...args)
        .then(response => {
          AuthenticatedController.storeSession(response);
          dispatch(this.retrieved(response));

          return response;
        })
        .catch(createSessionHandler(dispatch));
    };
  }

  retrieveSessionCompany(companyId) {
    return async dispatch => {
      try {
        return AuthenticatedController.get(
          SESSION_COMPANY,
          ObjectUtils.mapObjKeysToSnakeCase({ companyId }),
        );
      } catch (err) {
        commonHandler(dispatch)(err);
      }
    };
  }

  update(session: Partial<SessionObject>) {
    return {
      type: this.updateAction,
      session,
    };
  }

  setInviteToken(inviteToken, companyName) {
    return {
      type: this.setInviteTokenAction,
      inviteToken,
      companyName,
    };
  }

  resetInviteToken() {
    return {
      type: this.resetInviteTokenAction,
    };
  }

  clear(errorCode?: number | string) {
    /*
      This is technically more of a view state action
      but we need access to this particular reducer
    */
    return async dispatch => {
      dispatch(this.busy(this.clearSessionAction));
      try {
        await AuthenticatedController.clearSession();
        dispatch({
          type: this.clearSessionAction,
          errorMessage: errorCode ? ErrorMessages[errorCode] : '',
        });
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  validateEmailVerifyCode(
    ...args: Parameters<typeof SessionController.validateEmailVerifyCode>
  ) {
    return async dispatch => {
      const res = await SessionController.validateEmailVerifyCode(...args);

      if (res.success && res.user) {
        const session = AuthenticatedController.getSession();
        const up = { ...session, needsToVerifyEmail: false } as SessionObject;
        AuthenticatedController.storeSession(up);
        dispatch(this.retrieved(up));
      }

      return res;
    };
  }

  retrieveMyUser(
    { writeToStorage }: { writeToStorage?: boolean } = {
      writeToStorage: false,
    },
  ) {
    return async dispatch => {
      try {
        const payload = await UsersController.getMyUser();
        dispatch({ type: this.getMyUserAction, payload });

        const currentSession = AuthenticatedController.getSession();

        if (writeToStorage && currentSession) {
          AuthenticatedController.storeSession({
            ...currentSession,
            ...omit(payload, 'authToken'),
          });
        }

        return payload;
      } catch (e) {
        await commonHandler(dispatch)(e);
        throw e;
      }
    };
  }

  resendEmailVerifyCode() {
    return async () => SessionController.resendEmailVerifyCode();
  }
}

const sessionCRUDActions = new SessionCRUDActions();

export default sessionCRUDActions;

export const useSessionCRUDActions = getActionHookBuilder(sessionCRUDActions, [
  'signUp',
  'login',
  'requestResetPassword',
  'confirmResetPassword',
  'validateResetPasswordToken',
  'create',
  'createSemrush',
  'retrieveSessionCompany',
  'update',
  'setInviteToken',
  'resetInviteToken',
  'clear',
  'validateEmailVerifyCode',
  'resendEmailVerifyCode',
  'retrieveMyUser',
]);
